import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, MapPin, Search, Plus, List } from 'lucide-react';
import { categories, appScreenshots } from '../../data/mock';
import { PhoneMockup } from '../interactive/PhoneMockup';
import { useTranslation } from 'react-i18next';

export const PinGallery = () => {
  const { t } = useTranslation();
  return (
    <section id="pin-gallery" className="py-24 bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6">
            {t('pinGallery.title1')}
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {t('pinGallery.subtitle1')}
          </p>
        </div>

        {/* Pin Grid / Category Display */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
          {categories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, scale: 1.05 }}
              transition={{ delay: index * 0.05, type: "spring", stiffness: 150 }}
              className="bg-slate-50 rounded-3xl p-6 text-center shadow-sm hover:shadow-xl hover:shadow-blue-500/10 transition-all border border-slate-100 flex flex-col items-center justify-center gap-4 group cursor-pointer"
            >
              {/* Using the icon from the categories as the "Pin" visualization */}
              <div className="w-20 h-20 rounded-[1.5rem] bg-white border border-slate-200 flex items-center justify-center group-hover:border-[#0DA2E7] transition-colors relative shadow-sm">
                <img 
                  src={category.icon} 
                  alt={category.name} 
                  className="w-10 h-10 object-contain transition-transform group-hover:scale-110" 
                />
                {/* Small pin pointer decoration to make it look like a map pin */}
                <div className="absolute -bottom-2 w-4 h-4 bg-white border-b border-r border-slate-200 rotate-45 group-hover:border-[#0DA2E7] transition-colors" />
              </div>
              <div className="mt-2">
                <h4 className="font-semibold text-slate-900">{category.name}</h4>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Ventana Pin Feature Spotlight */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mt-24 lg:mt-32 max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-12 lg:gap-20 bg-white rounded-[2.5rem] p-8 lg:p-12 shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden cursor-default"
        >
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-[#0DA2E7]/10 rounded-full blur-3xl" />
          <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-orange-100 rounded-full blur-3xl" />
          
          <div className="flex-1 text-left relative z-10">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#0DA2E7] mb-2 block">
                {t('pinGallery.badge')}
              </span>
              <h3 className="text-3xl font-black text-slate-900 mb-4 leading-tight">
                {t('pinGallery.title2')}
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {t('pinGallery.desc2')}
              </p>
            
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="bg-[#FAF9F6] p-5 rounded-2xl border border-slate-200">
                  <h4 className="font-semibold text-slate-900 text-lg">{t('pinGallery.f1_title')}</h4>
                  <p className="text-slate-600 text-sm mt-1 leading-relaxed">{t('pinGallery.f1_desc')}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-lg">{t('pinGallery.f2_title')}</h4>
                  <p className="text-slate-600 text-sm mt-1 leading-relaxed">{t('pinGallery.f2_desc')}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="bg-slate-900 p-5 rounded-2xl shadow-md text-white">
                  <h4 className="font-semibold text-white text-lg">{t('pinGallery.f3_title')}</h4>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{t('pinGallery.f3_desc')}</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="flex-1 w-full flex justify-center relative z-10">
             <div className="relative w-64 md:w-72">
               <div className="absolute inset-0 rounded-full blur-3xl bg-blue-100 opacity-60 scale-150 -z-10" />
               <PhoneMockup currentScreen={appScreenshots.placeDetailFull || appScreenshots.placeDetail} />
             </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};
